import * as React from 'react';
import { MapDTO, TicketLocationDTO } from 'core/api';
import * as UI from 'core/ui';
import { MapUtils, MapConverter, Map } from 'core/ui';
import { SaleLocationStatus } from './SaleLocationStatus';
import { Guid } from 'core/infrastructure';
import { Box, HStack, VStack } from '@chakra-ui/react';
import LocationSelector from './LocationSelector';
import { PhoneOnly } from './PhoneOnly';
import { LargeDeviceOnly } from './LargeDeviceOnly';

function applyTableStatus(map: UI.Map, locationStatus: SaleLocationStatus): UI.Map {

	const locationStatuses = locationStatus.getAllSaleCenters().flatMap(x => x.SaleLocations);
	const tables = map.sprites.filter(x => x instanceof UI.Table) as UI.Table[];

	tables.forEach(table => {
		const status = locationStatuses.find(({ TicketLocation: loc }) =>
			loc.SaleCenterId === table.location.saleCenterId &&
			loc.SaleLocationName === table.location.saleLocationName
		);

		if (!status) throw new Error('El plano de mesas contiene alguna ubicación que ya no existe.');

		table.ticketHasBeenPrepared = status.TicketHasBeenPrepared;
		table.ticketIsInactive = status.IsInactive;
		table.displayName = status.DisplayName;

		var baseImageName = table.image.name.replace('_pr', '').replace('_bs', '');

		if (status.TicketHasBeenPresented) {
			table.image = locationStatus.getImageCache().getImage(`${baseImageName}_pr`);
		} else if (status.HasOpenedTickets) {
			table.image = locationStatus.getImageCache().getImage(`${baseImageName}_bs`);
		} else {
			table.image = locationStatus.getImageCache().getImage(`${baseImageName}`);
		}
	});

	return map;
}

function getScale(map: UI.Map, { clientHeight, clientWidth }: HTMLElement): number {
	switch (map.resizeMode) {
		case 'shrink': {
			const scaleX = Math.min(1, clientWidth / map.width);
			const scaleY = Math.min(1, clientHeight / map.height);
			return Math.min(scaleX, scaleY);
		}
		case 'vertical-fit':
			return clientHeight / map.height;
		case 'fit': {
			const scaleX = clientWidth / map.width;
			const scaleY = clientHeight / map.height;
			return Math.min(scaleX, scaleY);
		}
	}
}

function buildMaps(status: SaleLocationStatus, selectedMap: MapDTO) {
	return status.getAllMaps().map(m => ({
		id: m.Id,
		selected: m.Id === selectedMap.Id,
		Style: {
			Text: m.Name,
			ImageId: Guid.empty,
			BackColor: ''
		}
	}));
}

function findTicketLocation(status: SaleLocationStatus, saleLocation: UI.TableSaleLocation): TicketLocationDTO {
	return status.getAllSaleCenters()
		.flatMap(x => x.SaleLocations)
		.map(x => x.TicketLocation)
		.find(x => x.SaleCenterId === saleLocation.saleCenterId && x.SaleLocationName === saleLocation.saleLocationName)!;
}

type Props = {
	onSaleLocationSelected: (sl: TicketLocationDTO) => void;
	selected: MapDTO;
};

export function create(status: SaleLocationStatus) {
	return function MapsContent({ onSaleLocationSelected, selected }: Props) {

		const canvasRef = React.useRef<HTMLCanvasElement>(null);

		const getMap = React.useCallback(async (): Promise<UI.Map> => {
			const validLocations = status.getAllSaleCenters().flatMap(x => x.SaleLocations).map(x => x.TicketLocation);
			return await MapConverter.toMap(selected.XmlContent, status.getImageCache(), validLocations);
		}, [selected.XmlContent]);

		const drawMap = React.useCallback((map: Map) => {
			const canvas = canvasRef.current;
			if (!canvas || !canvas.parentElement) return;

			map = applyTableStatus(map, status);
			const scale = getScale(map, canvas.parentElement);

			canvas.width = map.width * scale * window.devicePixelRatio;
			canvas.height = map.height * scale * window.devicePixelRatio;
			canvas.style.width = `${map.width * scale}px`;
			canvas.style.height = `${map.height * scale}px`;

			map.drawOn(canvas);
		}, []);

		const handleCanvasClick = React.useCallback(async (e: React.MouseEvent<HTMLCanvasElement>) => {
			e.preventDefault();
			e.stopPropagation();

			const map = await getMap();
			const location = MapUtils.getLocationAt(e, canvasRef.current!, map);
			if (location) {

				// Dibujo el efecto de pulsación en la ubicación
				const table = MapUtils.findTableByLocation(map, location)!;
				table.clicked = true;
				drawMap(map);
				setTimeout(() => {
					table.clicked = false;
					drawMap(map);
				}, 50);

				const ticketLocation = findTicketLocation(status, location);
				onSaleLocationSelected(ticketLocation);
			}
		}, [getMap, onSaleLocationSelected, drawMap]);

		{/* Usamos useLayoutEffect para asegurarnos de que el canvas se dibuje antes
		de que se renderice el componente y así evitar el flicker en la pantalla. */}
		React.useLayoutEffect(() => {
			void (async () => {
				const map = await getMap();
				drawMap(map);
			})();
		}, [getMap, drawMap]);

		const handleMapSelected = React.useCallback(async (mapId: number) => {
			await status.selectMap(mapId);
		}, []);

		const mapBox = <Box width={'full'} height={'full'} display={'grid'} placeItems={'center'} overflowY={'auto'}>
			<canvas ref={canvasRef} onClick={handleCanvasClick} />
		</Box>;

		return (
			<>
				<PhoneOnly>
					<VStack width={'full'} height={'full'} gap={'3'}>
						<VStack width={'full'} paddingX={'2'} paddingTop={'2'}>
							<LocationSelector items={buildMaps(status, selected)} onItemSelected={handleMapSelected} />
						</VStack>
						{mapBox}
					</VStack>

				</PhoneOnly>
				<LargeDeviceOnly>
					<HStack gap={'6'} width={'full'} height={'full'}>
						<Box width={'auto'} height={'full'} paddingTop={'6'} paddingBottom={'6'} paddingLeft={'5'}>
							<LocationSelector items={buildMaps(status, selected)} onItemSelected={handleMapSelected} orientation={'vertical'} />
						</Box>
						{mapBox}
					</HStack>
				</LargeDeviceOnly>
			</>
		);
	};
}
